'use client';

type ViewMode = 'heatmap' | 'table' | 'backtest' | 'technical' | '3d';
export type VMode = 'classic' | 'blended';

interface Props {
  viewMode: ViewMode;
  factorCount: number;
  updatedAt?: string;
  /** V axis mode — optional. Lives on the 3D scatter overlay since 2026-05-08;
   *  retained on header for backwards compatibility / future use only. */
  vMode?: VMode;
  onVModeChange?: (mode: VMode) => void;
  /** Switch to 3D — full reset to "complete view" (clears highlights/filters). */
  onSelect3D: () => void;
  /** Switch to a non-3D view — soft reset (clears highlights/selection but
   *  keeps filters). */
  onSelectOther: (mode: Exclude<ViewMode, '3d'>) => void;
}

// 'backtest' removed from the home view-mode toggle 2026-06-01 — the backtest now
// lives as a first-class page at /pipeline?tab=backtest (Sharpe-first, with history).
// Kept in the ViewMode type + label map so saved state / deep links don't break.
const VIEW_MODES: ViewMode[] = ['3d', 'table', 'heatmap', 'technical'];

const VIEW_LABELS: Record<ViewMode, string> = {
  '3d': '3D',
  table: 'Table',
  heatmap: '2D Q-V',
  technical: '2D Q-M',
  backtest: 'Backtest',
};

/**
 * Top nav bar for the home page — branding pill + view-mode toggle group +
 * Research Desk link + last-refresh timestamp. Pure presentational; all
 * mode-switch behavior is driven by the two callbacks.
 */
export function AppHomeHeader({ viewMode, factorCount, updatedAt, vMode, onVModeChange, onSelect3D, onSelectOther }: Props) {
  return (
    <header className="flex-shrink-0 border-b border-[#1e293b] bg-[#0d1117] flex items-center gap-4 px-5 py-3 z-40">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.7)]" />
        <h1 className="text-white font-bold text-base tracking-tight">
          Triumph <span className="text-[10px] font-normal text-[#64748b] tracking-widest uppercase ml-1">Quant</span>
        </h1>
        <span className="hidden md:inline text-[9px] font-semibold tracking-[0.2em] uppercase ml-2" style={{ color: '#f59e0b' }}>
          {factorCount} Factor Model
        </span>
      </div>
      <div className="flex-1 flex justify-center">
        <div className="flex gap-1">
          {VIEW_MODES.map(mode => {
            const active = viewMode === mode;
            return (
              <button
                key={mode}
                onClick={() => (mode === '3d' ? onSelect3D() : onSelectOther(mode))}
                className={`text-[11px] font-semibold tracking-wide transition-all duration-150 cursor-pointer rounded-md px-3.5 py-1.5
                  ${active
                    ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                    : 'bg-[#111827] text-[#94a3b8] border border-[#1e293b] hover:text-white hover:border-[#334155]'}`}
              >
                {VIEW_LABELS[mode]}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {vMode != null && onVModeChange && <VModeToggle vMode={vMode} onChange={onVModeChange} />}
        <a
          href="/research-desk"
          className="text-[10px] font-semibold tracking-wide uppercase text-[#94a3b8] hover:text-amber-400 transition-colors hidden md:inline-block"
        >
          Research Desk →
        </a>
        <div className="text-[10px] text-[#64748b] tabular-nums hidden md:block" style={{ fontFamily: 'var(--font-mono)' }}>
          {updatedAt ? (
            <>
              Last: <span style={{ color: '#f59e0b' }}>{new Date(updatedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
              <span className="mx-1.5 text-[#334155]">·</span>
              Next: 6:00
            </>
          ) : '--'}
        </div>
      </div>
    </header>
  );
}

/**
 * Two-state V toggle. Classic = peer-z V (production). Blended = 50/50
 * peer-z + universe-z absolute. Recomputes composite + Alpha Zone live.
 *
 * Walkforward 2026-05-08: Blended (Definition C @α=0.5) lifts CAGR +2.1pp
 * over baseline at the cost of −0.075 Sharpe. Toggle is view-only —
 * production scoring stays Classic until explicitly switched.
 */
function VModeToggle({ vMode, onChange }: { vMode: VMode; onChange: (m: VMode) => void }) {
  return (
    <div className="hidden md:flex items-center gap-1 bg-[#0a0e14] border border-[#1e293b] rounded-md p-0.5">
      <button
        onClick={() => onChange('classic')}
        className={`text-[10px] font-semibold tracking-wide uppercase transition-colors px-2 py-1 rounded ${
          vMode === 'classic'
            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
            : 'text-[#94a3b8] hover:text-white border border-transparent'
        }`}
        title="V = peer-z multiples (production model)"
      >
        Classic V
      </button>
      <button
        onClick={() => onChange('blended')}
        className={`text-[10px] font-semibold tracking-wide uppercase transition-colors px-2 py-1 rounded ${
          vMode === 'blended'
            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
            : 'text-[#94a3b8] hover:text-white border border-transparent'
        }`}
        title="V = 50% peer-z + 50% universe-z absolute (asym + DCF + today-FV) — view-only; +2.1pp CAGR backtested"
      >
        Blended V
      </button>
    </div>
  );
}
